// Header.js
import React, {useState, useEffect, useContext} from 'react';
import logo from '../images/logo.png';
import musicIcon from '../images/music-icon.png';
import musicGif from '../images/music-playing.gif';
import {
    Link,
    useNavigate,
    useLocation
} from 'react-router-dom';
import { useTranslation } from "react-i18next";
import i18n from '../i18n';
import {
    Navbar,
    Container,
    Dropdown,
    Modal,
    Button
} from 'react-bootstrap';
import '../css/header.css';
import '../css/steamModal.css';
import { FaSteam, FaUser, FaCog, FaBug, FaShieldAlt, FaSignOutAlt } from "react-icons/fa";
import SocialBarHeader from "./SocialBarHeader";
import SearchBar from "./SearchBar";
import Swal from "sweetalert2";
import api from "../api";
import { AuthContext } from "../context/AuthContext";

const LANGUAGES = [
    { code: 'en', label: 'EN', flag: 'gb' },
    { code: 'ru', label: 'RU', flag: 'ru' },
    { code: 'ua', label: 'UA', flag: 'ua' },
    { code: 'ko', label: 'KO', flag: 'kr' },
    { code: 'fr', label: 'FR', flag: 'fr' },
];

const getFlag = (code) => {
    const lang = LANGUAGES.find((l) => l.code === code);
    return lang ? lang.flag : 'gb';
};

const Header = ({
    onHomeClick,
    onNewsClick,
    toggleMute,
    isMuted,
    isNavbarOpen,
    setIsNavbarOpen,
    closeNavbar
}) => {
    const initialLang = i18n.language.includes('-') ? i18n.language.split('-')[0] : i18n.language;
    const [selectedLanguage, setSelectedLanguage] = useState(initialLang);
    const [playerCount, setPlayerCount] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, isAuthReady, isAdmin, currentUserId, username, avatar, logOut } = useContext(AuthContext);

    useEffect(() => {
        i18n.changeLanguage(selectedLanguage);
    }, [selectedLanguage]);

    useEffect(() => {
        const handleLanguageChange = (lng) => {
            setSelectedLanguage(lng);
        };

        i18n.on('languageChanged', handleLanguageChange);

        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    }, []);

    const handleLanguageSelect = (language) => {
        const shortLang = language.includes('-') ? language.split('-')[0] : language;
        setSelectedLanguage(shortLang);
        i18n.changeLanguage(shortLang);
    };

    useEffect(() => {
        if (location.pathname !== '/auth/steam/callback') {
            const urlParams = new URLSearchParams(window.location.search);
            const accessToken = urlParams.get('access_token');

            if (accessToken) {
                localStorage.setItem('access_token', accessToken);
                window.history.replaceState(
                    {},
                    document.title,
                    window.location.pathname
                );
            }
        }
    }, [location.pathname]);

    const handleLogout = async () => {
        const accessToken = localStorage.getItem('access_token');

        try {
            await api.get(
                `${process.env.REACT_APP_API_URL}/api/auth/logOut`,
                {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );
        } catch (error) {
            Swal.fire({
                text: t('settings.error.invalidToken'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('survey.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
        }
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        logOut();
        navigate('/');
    };

    const handleToggle = () => {
        setIsNavbarOpen(!isNavbarOpen);
    };

    const prefetchServers = () => {
        ['/api/servers', '/api/servers/history?range=24h'].forEach((path) => {
            fetch(`${process.env.REACT_APP_API_URL}${path}`, {
                headers: { 'Content-Type': 'application/json' },
            }).catch(() => {});
        });
    };

    const handleLogin = () => {
        const redirectPath = window.location.pathname;
        setShowModal(true);
        localStorage.setItem('redirectPath', redirectPath);
    };

    const confirmLogin = () => {
        setShowModal(false);
        const redirectPath = window.location.pathname;
        window.location.href = `${process.env.REACT_APP_API_URL}/api/auth/steam?redirect=${encodeURIComponent(redirectPath)}`;
    };

    useEffect(() => {
        const fetchPlayerCount = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/api/servers/players?range=24h`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }

                const data = await response.json();
                setPlayerCount(data.online ?? 0);
            } catch (error) {
                setPlayerCount("N/A");
            }
        };

        fetchPlayerCount();
        const interval = setInterval(fetchPlayerCount, 60000);

        return () => clearInterval(interval);
    }, []);

    return (
        <>
            <Navbar expand="lg" fixed="top" variant="dark"
                    className="shadow-sm" id="header-nav">
                <Container fluid>
                    <div id="logo-container">
                        <Navbar.Brand onClick={() => { onHomeClick(); }}
                                      id="header-logo">
                            <img src={logo} alt="Logo" height="40" />
                        </Navbar.Brand>
                        <div className="player-count">
                        <span
                            className="status-indicator"
                            style={{
                                backgroundColor: typeof playerCount === 'number' && playerCount > 0 ? 'green' : 'red'
                            }}
                        ></span>
                            <span>
                            {playerCount === null
                                ? t('header.loading')
                                : ` ${playerCount}`}
                        </span>
                        </div>
                    </div>
                    {isNavbarOpen && (
                        <div className="d-lg-none mobile-settings-open">
                            <div onClick={toggleMute} className="music-icon">
                                <img src={isMuted ? musicGif : musicIcon}
                                     alt="Music Icon" width="24" height="24"/>
                            </div>

                            <Dropdown className="language-dropdown">
                                <Dropdown.Toggle variant="secondary" id="dropdown-basic-mobile">
                                    <img
                                        src={`https://flagcdn.com/w20/${getFlag(selectedLanguage)}.png`}
                                        alt=""
                                        className="lang-flag"
                                    />
                                    {selectedLanguage.toUpperCase()}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {LANGUAGES.map((lang) => (
                                        <Dropdown.Item key={lang.code} onClick={() => handleLanguageSelect(lang.code)}>
                                            <img
                                                src={`https://flagcdn.com/w20/${lang.flag}.png`}
                                                alt=""
                                                className="lang-flag"
                                            />
                                            {lang.label}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    )}
                    <Navbar.Collapse in={isNavbarOpen} id="navbar-nav">
                        <div className="navbar-expanded-content">
                            {isNavbarOpen && (
                                <SocialBarHeader />
                            )}
                            {isNavbarOpen && (
                                <div className="d-lg-none search-bar-mobile">
                                    <SearchBar />
                                </div>
                            )}
                            <ul className="navbar-nav mx-auto">
                                <li>
                                    <div className="menu_button" onClick={() => {
                                        onHomeClick();
                                        closeNavbar();
                                    }}>
                                        <span className="label custom-font-button">
                                            {t('header.home')}
                                        </span>
                                        <span className="icon"><span></span></span>
                                    </div>
                                </li>
                                <li>
                                    <div className="menu_button" onClick={() => {
                                        onNewsClick();
                                        closeNavbar();
                                    }}>
                                        <span className="label custom-font-button">
                                            {t('header.news')}
                                        </span>
                                        <span className="icon"><span></span></span>
                                    </div>
                                </li>
                                {/*<li>*/}
                                {/*    <div className="menu_button" onClick={() => {*/}
                                {/*        onGalleryClick();*/}
                                {/*        closeNavbar();*/}
                                {/*    }}>*/}
                                {/*        <span className="label custom-font-button">*/}
                                {/*            {t('header.gallery')}*/}
                                {/*        </span>*/}
                                {/*        <span className="icon"><span></span></span>*/}
                                {/*    </div>*/}
                                {/*</li>*/}
                                <li>
                                    <Link to="/characters" className="menu_button"
                                          onClick={closeNavbar}>
                                        <span className="label custom-font-button">
                                            {t('header.characters')}
                                        </span>
                                        <span className="icon"><span></span></span>
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/map" className="menu_button"
                                          onClick={closeNavbar}>
                                        <span className="label custom-font-button">
                                            {t('header.map')}
                                        </span>
                                        <span className="icon"><span></span></span>
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/servers" className="menu_button"
                                          onMouseEnter={prefetchServers}
                                          onFocus={prefetchServers}
                                          onClick={closeNavbar}>
                                        <span className="label custom-font-button">
                                            {t('header.servers')}
                                        </span>
                                        <span className="icon"><span></span></span>
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/posts" className="menu_button"
                                          onClick={closeNavbar}>
                                        <span className="label custom-font-button">
                                            {t('header.posts')}
                                        </span>
                                        <span className="icon"><span></span></span>
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/survey" className="menu_button"
                                          onClick={closeNavbar}>
                                        <span className="label custom-font-button">
                                            {t('header.survey')}
                                        </span>
                                        <span className="icon"><span></span></span>
                                    </Link>
                                </li>
                            </ul>
                        </div>
                    </Navbar.Collapse>

                    <div
                        className={`d-flex align-items-center settings-box ${isNavbarOpen ? 'navbar-open' : 'navbar-closed'}`}>
                        <div className="d-none d-lg-flex header-tools">
                            <div className="header-tool">
                                <SearchBar collapsible />
                            </div>

                            <span className="header-tools__divider" />

                            <button
                                type="button"
                                className="header-tool-btn"
                                onClick={toggleMute}
                                aria-label={t('header.sound')}
                                title={t('header.sound')}
                            >
                                <img src={isMuted ? musicGif : musicIcon}
                                     alt={t('header.sound')}/>
                            </button>

                            <span className="header-tools__divider" />

                            <Dropdown className="language-dropdown">
                                <Dropdown.Toggle variant="secondary" className="header-lang-toggle">
                                    <img
                                        src={`https://flagcdn.com/w20/${getFlag(selectedLanguage)}.png`}
                                        alt=""
                                        className="lang-flag"
                                    />
                                    {selectedLanguage.toUpperCase()}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {LANGUAGES.map((lang) => (
                                        <Dropdown.Item key={lang.code} onClick={() => handleLanguageSelect(lang.code)}>
                                            <img
                                                src={`https://flagcdn.com/w20/${lang.flag}.png`}
                                                alt=""
                                                className="lang-flag"
                                            />
                                            {lang.label}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                        <div id="nav-bar-button-info">
                            {isAuthReady && isAuthenticated === false && (
                                <button onClick={handleLogin} className="header-login">
                                    <FaSteam className="header-login__icon"/>
                                    <span>{t('header.login')}</span>
                                </button>
                            )}

                            {isAuthReady && isAuthenticated === true && username && (
                                <Dropdown align="end">
                                    <Dropdown.Toggle variant="secondary" id="dropdown-user" className="header-avatar" title={username}>
                                        <span className="header-avatar__fallback">{username.charAt(0).toUpperCase()}</span>
                                        {avatar && (
                                            <img
                                                className="header-avatar__img"
                                                src={avatar}
                                                alt={username}
                                                onError={(e) => {
                                                    e.currentTarget.style.display = 'none';
                                                }}
                                            />
                                        )}
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu>
                                        <Dropdown.Item as={Link} to={`/user/${currentUserId}`}>
                                            <FaUser className="dd-icon" />
                                            {t('header.myProfile')}
                                        </Dropdown.Item>
                                        <Dropdown.Item as={Link} to="/settings">
                                            <FaCog className="dd-icon" />
                                            {t('header.settings')}
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            as="a"
                                            href="https://superpeople.dev/bugs-and-ideas"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <FaBug className="dd-icon" />
                                            {t('header.bugsAndIdeas')}
                                        </Dropdown.Item>
                                        {isAdmin && (
                                            <Dropdown.Item as={Link} to="/spa">
                                                <FaShieldAlt className="dd-icon" />
                                                {t('header.adminPanel')}
                                            </Dropdown.Item>
                                        )}
                                        <Dropdown.Divider/>
                                        <Dropdown.Item onClick={handleLogout}>
                                            <FaSignOutAlt className="dd-icon" />
                                            {t('header.logout')}
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            )}
                        </div>
                        <Navbar.Toggle aria-controls="navbar-nav" onClick={handleToggle}/>
                    </div>
                </Container>
            </Navbar>

            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('auth.steamLoginTitle')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{t('auth.steamLoginMessage')}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        {t('auth.cancel')}
                    </Button>
                    <Button variant="primary" onClick={confirmLogin}>
                        {t('auth.proceed')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default Header;